<template>
  <div class="sidebar-section">
    <!-- Section title -->
    <div
      v-if="title && !collapsed"
      class="sidebar-section__title"
    >
      {{ title }}
    </div>

    <!-- Items -->
    <ul class="sidebar-section__list">
      <li
        v-for="(item, index) in items"
        :key="itemKey(item, index)"
        class="sidebar-section__item"
      >
        <!-- Item dengan submenu -->
        <div v-if="item.menu && item.menu.length" class="sidebar-submenu">
          <button
            type="button"
            class="sidebar-link sidebar-link--group"
            :class="{ 'sidebar-link--open': isOpen(item) }"
            @click="toggleGroup(item)"
          >
            <span class="sidebar-link__icon" v-html="iconSvg(item.icon)" />
            <span v-if="!collapsed" class="sidebar-link__label">
              {{ item.label }}
            </span>
            <span
              v-if="!collapsed"
              class="sidebar-link__chevron"
              :class="{ 'sidebar-link__chevron--open': isOpen(item) }"
            >
              ›
            </span>
          </button>

          <!-- Submenu -->
          <Transition name="submenu">
            <ul
              v-if="isOpen(item) && !collapsed"
              class="sidebar-submenu__list"
            >
              <li
                v-for="(sub, subIndex) in item.menu"
                :key="subKey(sub, subIndex)"
              >
                <router-link
                  :to="sub.to"
                  class="sidebar-link sidebar-link--sub"
                  active-class="sidebar-link--active"
                >
                  <span
                    v-if="sub.icon"
                    class="sidebar-link__icon"
                    v-html="iconSvg(sub.icon)"
                  />
                  <span class="sidebar-link__label">{{ sub.label }}</span>
                </router-link>
              </li>
            </ul>
          </Transition>
        </div>

        <!-- Item biasa -->
        <router-link
          v-else
          :to="item.to"
          class="sidebar-link"
          active-class="sidebar-link--active"
          :title="collapsed ? item.label : null"
        >
          <span class="sidebar-link__icon" v-html="iconSvg(item.icon)" />
          <span v-if="!collapsed" class="sidebar-link__label">
            {{ item.label }}
          </span>

          <!-- ✅ Badge notifikasi -->
          <span
            v-if="item.badge && item.badge > 0"
            class="sidebar-link__badge"
            :class="{ 'sidebar-link__badge--collapsed': collapsed }"
          >
            {{ item.badge > 99 ? '99+' : item.badge }}
          </span>
        </router-link>
      </li>
    </ul>
  </div>
</template>

<script>
export default {
  name: 'SidebarSection',

  props: {
    title: { type: String, default: '' },
    items: { type: Array, default: () => [] },
    collapsed: { type: Boolean, default: false }
  },

  data() {
    return {
      openGroups: {}
    }
  },

  watch: {
    /**
     * ✅ KUNCI: setiap route berubah (termasuk dari Quick Actions),
     * auto-buka semua group yang punya child cocok dengan route aktif.
     */
    '$route.path': {
      immediate: true,
      handler() {
        this.syncOpenGroupsWithRoute()
      }
    }
  },

  methods: {
    itemKey(item, index) {
      return item.to || item.label || `item-${index}`
    },

    subKey(sub, index) {
      return sub.to || sub.label || `sub-${index}`
    },

    /**
     * Cek apakah salah satu child di menu cocok dengan route aktif.
     */
    hasActiveChild(item) {
      if (!item.menu || !item.menu.length) return false
      return item.menu.some((child) => this.isActive(child.to))
    },

    /**
     * Cek apakah path aktif (persis atau prefix).
     */
    isActive(path) {
      if (!path) return false
      return (
        this.$route.path === path ||
        this.$route.path.startsWith(`${path}/`)
      )
    },

    /**
     * Buka group kalau ada child aktif.
     * Panggil setiap kali route berubah.
     */
    syncOpenGroupsWithRoute() {
      const nextOpen = { ...this.openGroups }
      let changed = false

      this.items.forEach((item) => {
        if (!item.menu || !item.menu.length) return

        const key = item.label || item.to

        if (this.hasActiveChild(item) && !nextOpen[key]) {
          nextOpen[key] = true
          changed = true
        }
      })

      if (changed) {
        this.openGroups = nextOpen
      }
    },

    isOpen(item) {
      const key = item.label || item.to
      // Kalau ada child aktif → paksa buka (walau user pernah tutup)
      if (this.hasActiveChild(item)) return true
      return this.openGroups[key] === true
    },

    toggleGroup(item) {
      const key = item.label || item.to
      // Kalau sedang ada child aktif & user klik → tetap izinkan tutup manual
      this.openGroups = {
        ...this.openGroups,
        [key]: !this.openGroups[key]
      }
    },

    iconSvg(icon) {
      if (!icon) return ''
      return `<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path fill="currentColor" d="${icon}"/></svg>`
    }
  }
}
</script>


<style scoped lang="scss">
.sidebar-section {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.sidebar-section__title {
  padding: 6px 12px;
  color: var(--color-text-secondary);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
}

.sidebar-section__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.sidebar-section__item {
  position: relative;
}

/* Link menu */
.sidebar-link {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  color: var(--color-text);
  font-size: 13px;
  font-weight: 500;
  text-decoration: none;
  transition: background-color 0.2s ease, color 0.2s ease;
  border: none;
  background: transparent;
  width: 100%;
  text-align: left;
  cursor: pointer;

  &:hover {
    background-color: var(--color-surface-hover);
    color: var(--color-text);
  }

  &:focus-visible {
    outline: 2px solid var(--color-button);
    outline-offset: -2px;
  }

  &--active {
    background-color: var(--color-surface-hover);
    color: var(--color-button);
    font-weight: 600;

    .sidebar-link__icon {
      color: var(--color-button);
    }
  }

  &--group {
    justify-content: flex-start;
  }

  &--sub {
    padding-left: 44px;
    font-size: 12.5px;
  }

  &__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    color: var(--color-text-secondary);

    :deep(svg) {
      width: 20px;
      height: 20px;
    }
  }

  &__label {
    flex: 1;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__chevron {
    font-size: 16px;
    transition: transform 0.2s ease;
    color: var(--color-text-secondary);

    &--open {
      transform: rotate(90deg);
    }
  }

  /* Notification badge */
  &__badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    margin-left: auto;
    border-radius: 10px;
    background-color: #ef4444;
    color: #ffffff;
    font-size: 11px;
    font-weight: 700;
    line-height: 1;
    flex-shrink: 0;

    &--collapsed {
      position: absolute;
      top: 4px;
      right: 4px;
      min-width: 16px;
      height: 16px;
      padding: 0 4px;
      font-size: 9px;
      margin-left: 0;
    }
  }
}

/* Submenu */
.sidebar-submenu {
  display: flex;
  flex-direction: column;
}

.sidebar-submenu__list {
  list-style: none;
  margin: 2px 0 4px;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

/* Submenu transition */
.submenu-enter-active,
.submenu-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.submenu-enter-from,
.submenu-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

/* Collapsed sidebar */
.sidebar--collapsed .sidebar-link {
  justify-content: center;
  padding: 10px;

  &--sub {
    padding-left: 10px;
  }
}

/* Reduced motion */
@media (prefers-reduced-motion: reduce) {
  .sidebar-link,
  .sidebar-link__chevron,
  .submenu-enter-active,
  .submenu-leave-active {
    transition: none !important;
  }
}
</style>
